import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Download, AlertTriangle, RotateCcw, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useFeatureMode, isFinancialExecutionAllowed } from "../hooks/useFeatureMode";
import SafetyFreezeNotice from "./SafetyFreezeNotice";

export default function FailedPaymentsPanel() {
  const featureMode = useFeatureMode();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const reload = async () => {
    try {
      const res = await apiClient.get("/billing/failed-payments");
      setData(res.data);
    } catch {
      setData({ records: [], summary: { total: 0 } });
    }
  };

  useEffect(() => {
    let cancelled = false;
    apiClient.get("/billing/failed-payments")
      .then(res => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData({ records: [], summary: { total: 0 } }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const runAction = async (record, action) => {
    const id = record.id;
    setActingId(`${id}:${action}`);
    try {
      await apiClient.post(`/billing/failed-payments/${id}/${action}`);
      const labels = { retry: "Retry scheduled", "send-link": "Payment-update link queued", resolve: "Marked resolved" };
      toast.success(labels[action] || "Done");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setActingId(null);
    }
  };

  const handleExport = async () => {
    try {
      const res = await apiClient.get("/billing/failed-payments.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `failed_payments_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {}
  };

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="p-6 text-sm text-slate-500">Loading failed payments…</CardContent>
      </Card>
    );
  }

  const records = data?.records || [];
  const summary = data?.summary || { total: 0 };

  // Package 0 Safety Freeze — retry / send-link controls disabled in statements-only mode.
  if (!isFinancialExecutionAllowed(featureMode)) {
    return (
      <Card className="border-slate-200" data-testid="failed-payments-panel-disabled">
        <CardContent className="p-4">
          <SafetyFreezeNotice
            title="Failed-payment recovery is not part of the current release"
            message="Payment retries and donor payment-update links are not part of the current release."
            testId="failed-payments-safety-notice"
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="failed-payments-panel">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <h3 className="font-heading font-semibold text-slate-900">Failed-Payment Recovery</h3>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-failed-payments">
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-4" data-testid="failed-payments-summary">
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="font-heading font-black text-xl text-slate-900">{summary.total}</div>
            <div className="text-[11px] text-slate-500 mt-1 uppercase tracking-wide">Total</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="font-heading font-black text-xl text-amber-600">{summary.retryingCount || 0}</div>
            <div className="text-[11px] text-slate-500 mt-1 uppercase tracking-wide">Retrying</div>
          </div>
          <div className="rounded-lg bg-slate-50 p-3">
            <div className="font-heading font-black text-xl text-red-600">{summary.cancelledAfterFailures || 0}</div>
            <div className="text-[11px] text-slate-500 mt-1 uppercase tracking-wide">Cancelled After Failures</div>
          </div>
        </div>
        {records.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center" data-testid="failed-payments-empty">No failed payments in scope.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="failed-payments-table">
              <thead className="text-xs uppercase text-slate-500 border-b">
                <tr>
                  <th className="text-left py-2">Tracking</th>
                  <th className="text-left py-2">Donor</th>
                  <th className="text-left py-2">Amount</th>
                  <th className="text-left py-2">Fundraiser</th>
                  <th className="text-left py-2">Campaign</th>
                  <th className="text-left py-2">Status</th>
                  <th className="text-right py-2">Failures</th>
                  <th className="text-left py-2">Last Failed</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => {
                  const isBusy = (suffix) => actingId === `${r.id}:${suffix}`;
                  return (
                  <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50">
                    <td className="py-2 font-mono text-xs">{r.externalTrackingId}</td>
                    <td className="py-2">{r.firstName} {r.lastName}</td>
                    <td className="py-2">£{r.amount}</td>
                    <td className="py-2">{r.fundraiserName}</td>
                    <td className="py-2">{r.campaignName}</td>
                    <td className="py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${r.cancelledAfterFailures ? "bg-red-100 text-red-700" : r.paymentStatus === "retrying" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>
                        {String(r.paymentStatus || "—").replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="py-2 text-right">{r.failedPaymentCount || 0}</td>
                    <td className="py-2 text-xs text-slate-500">{r.lastFailedPaymentAt ? r.lastFailedPaymentAt.slice(0, 10) : "—"}</td>
                    <td className="py-2">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="sm" className="h-7 px-2"
                          disabled={!!actingId}
                          onClick={() => runAction(r, "retry")}
                          data-testid={`retry-${r.id}`} title="Retry now">
                          <RotateCcw className={`h-3.5 w-3.5 ${isBusy("retry") ? "animate-spin" : ""}`} />
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 px-2"
                          disabled={!!actingId}
                          onClick={() => runAction(r, "send-link")}
                          data-testid={`send-link-${r.id}`} title="Send payment update link">
                          <Mail className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 px-2"
                          disabled={!!actingId}
                          onClick={() => runAction(r, "resolve")}
                          data-testid={`resolve-${r.id}`} title="Mark resolved">
                          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
